import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';

/**
 * Preguntas del Test DISC con sus 4 características
 * Según la imagen oficial del test DISC con 28 grupos de palabras
 * Formato: { id, opciones: [palabra1, palabra2, palabra3, palabra4] }
 */
const PREGUNTAS_DISC = [
  { id: 1, opciones: ['Entusiasta(a)', 'Extrovertido(a)', 'Lógico(a)', 'Apacible'] },
  { id: 2, opciones: ['Cauteloso(a)', 'Decidido(a)', 'Receptivo(a)', 'Bondadoso(a)'] },
  { id: 3, opciones: ['Amigable', 'Preciso(a)', 'Temeroso(a)', 'Tranquilo(a)'] },
  { id: 4, opciones: ['Elocuente', 'Controlado(a)', 'Tolerante', 'Decisivo(a)'] },
  { id: 5, opciones: ['Atrevido(a)', 'Conversador(a)', 'Comunicativo(a)', 'Moderado(a)'] },
  { id: 6, opciones: ['Ameno(a)', 'Consciente(a)', 'Investigador(a)', 'Alegre'] },
  { id: 7, opciones: ['Enérgico(a)', 'Cuidadoso(a)', 'Humano(a)', 'Sensible'] },
  { id: 8, opciones: ['Extrovertido(a)', 'Raro(a)', 'Constante', 'Impaciente'] },
  { id: 9, opciones: ['Discreto(a)', 'Complaciente', 'Encantador(a)', 'Valiente'] },
  { id: 10, opciones: ['Valeroso(a)', 'Ánima a los demás', 'Decisivo(a)', 'Perfeccionista'] },
  { id: 11, opciones: ['Reservado(a)', 'Atento(a)', 'Rápido(a)', 'Alegre'] },
  { id: 12, opciones: ['Estimulante', 'Gentil', 'Perceptivo(a)', 'Independiente'] },
  { id: 13, opciones: ['Comprometido(a)', 'Considerado(a)', 'Alegre', 'Sagaz'] },
  { id: 14, opciones: ['Manipulador(a)', 'Obediente', 'Que se Firmes', 'Alentador(a)'] },
  { id: 15, opciones: ['Popular', 'Reflexivo(a)', 'Tenaz', 'Intrépido(a)'] },
  { id: 16, opciones: ['Analítico(a)', 'Audaz', 'Leal', 'Animador(a)'] },
  { id: 17, opciones: ['Sociable', 'Paciente', 'Autosuficiente', 'Certero(a)'] },
  { id: 18, opciones: ['Adaptable', 'Resuelto(a)', 'Prevenido(a)', 'Vivaz'] },
  { id: 19, opciones: ['Agresivo(a)', 'Impetuoso(a)', 'Amazoto(a)', 'Conviviente'] },
  { id: 20, opciones: ['De trato Fácil', 'Compasivo', 'Cauteloso(a)', 'Hábil de Efecto'] },
  { id: 21, opciones: ['Convencedor', 'Generoso(a)', 'Competitivo', 'Persistente'] },
  { id: 22, opciones: ['Impulsivo(a)', 'Cuida de Detalles', 'Enérgico(a)', 'Amigable'] },
  { id: 23, opciones: ['Sociable', 'Sensible(a)', 'Vigoroso(a)', 'Exigente'] },
  { id: 24, opciones: ['Cautivador(a)', 'Contento(a)', 'Exigente', 'Apegado a lo normal'] },
  { id: 25, opciones: ['Le agrada discutir', 'Metódico(a)', 'Bondadoso(a)', 'Desenvuelto(a)'] },
  { id: 26, opciones: ['Amable', 'Jovial', 'Preciso(a)', 'Directo(a)'] },
  { id: 27, opciones: ['Inquieto(a)', 'Amable', 'Elocuente', 'Audaz'] },
  { id: 28, opciones: ['Valiente', 'Pionero(a)', 'Colaborador', 'Persuasivo(a)'] }
];

/**
 * Componente para realizar el Test DISC
 * Permite al usuario seleccionar la característica que MÁS y MENOS lo describe
 */
export default function TestDiscForm({ onSubmit, loading }) {
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestasMas, setRespuestasMas] = useState(Array(28).fill(null));
  const [respuestasMenos, setRespuestasMenos] = useState(Array(28).fill(null));

  const pregunta = PREGUNTAS_DISC[preguntaActual];
  const progreso = ((preguntaActual + 1) / PREGUNTAS_DISC.length) * 100;
  const esUltimaPregunta = preguntaActual === PREGUNTAS_DISC.length - 1;
  const respuestaCompleta = respuestasMas[preguntaActual] !== null && respuestasMenos[preguntaActual] !== null;

  const handleSeleccionMas = (indice) => {
    if (respuestasMenos[preguntaActual] === indice + 1) {
      // Si ya está seleccionado como MENOS, no permitir
      return;
    }
    const nuevasRespuestas = [...respuestasMas];
    nuevasRespuestas[preguntaActual] = indice + 1; // 1-indexed
    setRespuestasMas(nuevasRespuestas);
  };

  const handleSeleccionMenos = (indice) => {
    if (respuestasMas[preguntaActual] === indice + 1) {
      // Si ya está seleccionado como MÁS, no permitir
      return;
    }
    const nuevasRespuestas = [...respuestasMenos];
    nuevasRespuestas[preguntaActual] = indice + 1; // 1-indexed
    setRespuestasMenos(nuevasRespuestas);
  };

  const handleAnterior = () => {
    if (preguntaActual > 0) {
      setPreguntaActual(preguntaActual - 1);
    }
  };

  const handleSiguiente = () => {
    if (respuestaCompleta) {
      if (esUltimaPregunta) {
        // Enviar test
        onSubmit({
          respuestasMas,
          respuestasMenos
        });
      } else {
        setPreguntaActual(preguntaActual + 1);
      }
    }
  };

  const todasRespondidas = respuestasMas.every(r => r !== null) && respuestasMenos.every(r => r !== null);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Barra de progreso */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="font-medium text-neutral-700">
            Pregunta {preguntaActual + 1} de {PREGUNTAS_DISC.length}
          </span>
          <span className="text-neutral-600">
            {Math.round(progreso)}% completado
          </span>
        </div>
        <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${progreso}%` }}
          />
        </div>
      </div>

      {/* Tarjeta de pregunta */}
      <Card className="p-6">
        <div className="space-y-6">
          {/* Instrucciones */}
          <div className="text-center space-y-2">
            <h2 className="text-xl font-bold text-neutral-800">
              Grupo {pregunta.id}
            </h2>
            <p className="text-neutral-600">
              De las siguientes características, selecciona:
            </p>
            <div className="flex justify-center gap-8 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-green-500"></div>
                <span className="text-sm font-medium text-neutral-700">La que MÁS te describe</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-red-500"></div>
                <span className="text-sm font-medium text-neutral-700">La que MENOS te describe</span>
              </div>
            </div>
          </div>

          {/* Opciones */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {pregunta.opciones.map((opcion, indice) => {
              const seleccionadaMas = respuestasMas[preguntaActual] === indice + 1;
              const seleccionadaMenos = respuestasMenos[preguntaActual] === indice + 1;
              const deshabilitada = (respuestasMas[preguntaActual] && !seleccionadaMas && !seleccionadaMenos) ||
                                   (respuestasMenos[preguntaActual] && !seleccionadaMas && !seleccionadaMenos);

              return (
                <div
                  key={indice}
                  className={`
                    relative p-6 rounded-lg border-2 transition-all duration-200
                    ${seleccionadaMas ? 'border-green-500 bg-green-50' : ''}
                    ${seleccionadaMenos ? 'border-red-500 bg-red-50' : ''}
                    ${!seleccionadaMas && !seleccionadaMenos ? 'border-neutral-200 hover:border-primary-300 hover:bg-neutral-50' : ''}
                    ${deshabilitada ? 'opacity-40' : ''}
                  `}
                >
                  {/* Texto de la opción */}
                  <div className="text-center mb-4">
                    <span className="text-lg font-semibold text-neutral-800">
                      {opcion}
                    </span>
                  </div>

                  {/* Botones MÁS / MENOS */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSeleccionMas(indice)}
                      disabled={respuestasMenos[preguntaActual] === indice + 1}
                      className={`
                        flex-1 py-2 px-4 rounded-md font-medium transition-all
                        ${seleccionadaMas 
                          ? 'bg-green-500 text-white shadow-md' 
                          : 'bg-white border border-green-500 text-green-600 hover:bg-green-50'
                        }
                        disabled:opacity-40 disabled:cursor-not-allowed
                      `}
                    >
                      {seleccionadaMas ? '✓ MÁS' : 'MÁS'}
                    </button>
                    <button
                      onClick={() => handleSeleccionMenos(indice)}
                      disabled={respuestasMas[preguntaActual] === indice + 1}
                      className={`
                        flex-1 py-2 px-4 rounded-md font-medium transition-all
                        ${seleccionadaMenos 
                          ? 'bg-red-500 text-white shadow-md' 
                          : 'bg-white border border-red-500 text-red-600 hover:bg-red-50'
                        }
                        disabled:opacity-40 disabled:cursor-not-allowed
                      `}
                    >
                      {seleccionadaMenos ? '✓ MENOS' : 'MENOS'}
                    </button>
                  </div>

                  {/* Indicador de selección */}
                  {(seleccionadaMas || seleccionadaMenos) && (
                    <div className="absolute -top-2 -right-2">
                      <CheckCircle 
                        className={`w-6 h-6 ${seleccionadaMas ? 'text-green-500' : 'text-red-500'}`}
                        fill="currentColor"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Advertencia si falta seleccionar */}
          {!respuestaCompleta && (
            <div className="text-center">
              <p className="text-sm text-amber-600">
                ⚠️ Debes seleccionar una opción como MÁS y otra como MENOS para continuar
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Botones de navegación */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={handleAnterior}
          disabled={preguntaActual === 0}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Anterior
        </Button>

        <div className="text-sm text-neutral-600">
          {todasRespondidas ? '✅ Test completado' : `${respuestasMas.filter(r => r !== null).length} de ${PREGUNTAS_DISC.length} respondidas`}
        </div>

        <Button
          onClick={handleSiguiente}
          disabled={!respuestaCompleta || loading}
          className="gap-2"
        >
          {esUltimaPregunta ? (
            <>
              {loading ? 'Procesando...' : 'Finalizar Test'}
              <CheckCircle className="w-4 h-4" />
            </>
          ) : (
            <>
              Siguiente
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
